import Spinner from 'Components/Spinner/Spinner';
import { FunctionComponent, useMemo } from 'react';
import DataTable, { TableColumn, createTheme } from 'react-data-table-component';
import { GoLock, GoUnlock } from 'react-icons/go';

import { EnableButton, MerchantsDataTableContainer, MessageWrapper } from '../styles';
import EmptyComponent from './EmptyComponent';
import ErrorComponent from './ErrorComponent';
import { TableStyleSettings, TableThemeSettings } from './constants';
import { MerchantsList } from './types';

// createTheme creates a new theme named solarized that overrides the build in dark theme
createTheme('polarized', TableThemeSettings);

interface Props {
  merchantsList?: MerchantsList | null;
  fetchListLoader: boolean;
  isErrorRequest?: boolean;
  togglePayoutStatus: (merchantId: string, payoutStatus: string) => void;
}

function MerchantsDataTable({ merchantsList, fetchListLoader, isErrorRequest, togglePayoutStatus }: Props) {
  const EnableIcon = GoUnlock as FunctionComponent;
  const DisableIcon = GoLock as FunctionComponent;

  const columns: TableColumn<MerchantsList[number]>[] = useMemo(
    () => [
      {
        name: 'Merchant ID',
        selector: (row: MerchantsList[number]) => row.merchantId,
        width: '200px',
      },
      {
        name: 'Business name',
        selector: (row: MerchantsList[number]) => row.businessProfile.businessName,
        width: '300px',
      },
      {
        name: 'Brand name',
        selector: (row: MerchantsList[number]) => row.businessProfile.brandName,
        width: '250px',
      },
      {
        name: 'Country',
        selector: (row: MerchantsList[number]) => row.businessProfile.tradingCountry,
        width: '80px',
      },
      {
        name: 'Payouts',
        selector: (row: MerchantsList[number]) => row.payoutDetails?.payoutStatus,
        width: '80px',
      },
      {
        cell: (row: MerchantsList[number]) => (
          <div className="hover-button-wrapper">
            <EnableButton
              className="hover-button"
              title={`${row.payoutDetails?.payoutStatus === 'Enabled' ? 'Disable' : 'Enable'} payouts`}
              onClick={() =>
                row.payoutDetails ? togglePayoutStatus(row.merchantId, row.payoutDetails.payoutStatus) : null
              }
            >
              {row.payoutDetails?.payoutStatus === 'Enabled' ? <DisableIcon /> : <EnableIcon />}
            </EnableButton>
          </div>
        ),
        width: '100px',
        center: true,
      },
    ],
    [DisableIcon, EnableIcon, togglePayoutStatus],
  );

  return (
    <MerchantsDataTableContainer>
      <DataTable
        pagination
        responsive
        columns={columns}
        data={merchantsList ?? []}
        customStyles={TableStyleSettings}
        theme="polarized"
        progressPending={fetchListLoader}
        progressComponent={
          <MessageWrapper>
            <Spinner color="#0016D1" height="40px" width="40px" />
          </MessageWrapper>
        }
        noDataComponent={
          isErrorRequest ? (
            <MessageWrapper>
              <ErrorComponent />
            </MessageWrapper>
          ) : (
            <MessageWrapper>
              <EmptyComponent />
            </MessageWrapper>
          )
        }
      />
    </MerchantsDataTableContainer>
  );
}

export default MerchantsDataTable;
